'use client'

import { Button } from '@repo/ui'
import { cn } from '@repo/ui'
import {
  ChevronDown,
  ChevronUp,
  Clock3,
  Mic,
  Paperclip,
  RotateCcw,
  Send,
  Sparkles,
  Square,
  User,
  X,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { useTestAgent, useTranscribeAgentTestAudio } from '@/hooks/use-agents'
import { useFollowUpConfig } from '@/hooks/use-follow-ups'
import { useLeads } from '@/hooks/use-leads'
import type {
  AgentTestAction,
  AgentTestMessage,
  AgentToolMessage,
  PendingBookingConfirmation,
} from '@/types/agents'
import type { ContactListItem } from '@/types/leads'

interface TestPanelProps {
  agentId: string
  agentName: string
  orgId: string
}

const ACTION_LABELS: Record<string, (action: AgentTestAction) => string> = {
  BOOK_APPOINTMENT: (a) => {
    const scheduledAt =
      typeof a.scheduledAt === 'string' ? new Date(a.scheduledAt) : null
    const formatted =
      scheduledAt && !Number.isNaN(scheduledAt.getTime())
        ? scheduledAt.toLocaleString('pt-BR', {
            timeZone: 'America/Sao_Paulo',
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })
        : String(a.scheduledAt ?? '')
    return `${a.booked ? 'Agendou' : 'Agendaria'}: ${String(a.title ?? '')} em ${formatted}`
  },
  CHECK_AVAILABILITY: (a) =>
    `Verificou disponibilidade em ${String(a.date ?? '')}`,
  TRANSFER_TO_HUMAN: () => 'Transferiria para humano',
  MARK_HOT_LEAD: () => 'Marcaria como hot lead',
  ADD_TAG: (a) => `Tag: ${String(a.tagName ?? '')}`,
  ADD_NOTE: () => 'Nota interna',
  ENROLL_CADENCE: (a) => `Cadência: ${String(a.cadenceName ?? '')}`,
  SAVE_FIELD: (a) =>
    `Campo: ${String(a.field ?? '')} = ${String(a.value ?? '')}`,
  CALL_MCP_TOOL: (a) =>
    `Consultou MCP: ${String(a.toolName ?? a.mcpToolId ?? '')}`,
}

const ACTION_ICONS: Record<string, string> = {
  BOOK_APPOINTMENT: '📅',
  CHECK_AVAILABILITY: '🔍',
  TRANSFER_TO_HUMAN: '👤',
  MARK_HOT_LEAD: '🔥',
  ADD_TAG: '🏷',
  ADD_NOTE: '📝',
  ENROLL_CADENCE: '📨',
  SAVE_FIELD: '💾',
  CALL_MCP_TOOL: '🔧',
}

function getInitials(name: string | null): string {
  if (!name) return '?'
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('')
}

function ActionsDisplay({ actions }: { actions: AgentTestAction[] }) {
  const [expanded, setExpanded] = useState(false)
  if (!actions || actions.length === 0) return null

  return (
    <div className="mt-1.5">
      <button
        type="button"
        onClick={() => setExpanded((p) => !p)}
        className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-xs transition-colors"
      >
        {expanded ? (
          <ChevronUp className="size-3" />
        ) : (
          <ChevronDown className="size-3" />
        )}
        {actions.length} {actions.length === 1 ? 'ação' : 'ações'}{' '}
        {expanded
          ? ''
          : actions.some((action) => action.booked)
            ? '(executado)'
            : '(simulado)'}
      </button>
      {expanded && (
        <div className="mt-1.5 flex flex-col gap-1">
          {actions.map((action, i) => (
            <div
              key={i}
              className="bg-muted flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs"
            >
              <span>{ACTION_ICONS[action.type] ?? '⚙️'}</span>
              <span className="text-foreground">
                {ACTION_LABELS[action.type]?.(action) ?? action.type}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

type Msg = {
  role: 'user' | 'assistant'
  content: string
  mediaUrl?: string
  mediaType?: 'image' | 'audio'
  transcription?: string
  actions?: AgentTestAction[]
  toolMessages?: AgentToolMessage[]
  followUp?: {
    step: number
    delayMinutes: number
    mode: 'TEXT' | 'AI'
  }
}

function renderFollowUpContent(content: string, lead: ContactListItem | null) {
  const values: Record<string, string> = {
    nome: lead?.name ?? '',
    empresa: lead?.company ?? '',
    telefone: lead?.phone ?? '',
    email: lead?.email ?? '',
  }

  return content.replace(
    /\{\{\s*(nome|empresa|telefone|email)\s*\}\}/gi,
    (_, key: string) => values[key.toLowerCase()] ?? ''
  )
}

function formatDelay(minutes: number) {
  if (minutes >= 1440 && minutes % 1440 === 0) {
    const days = minutes / 1440
    return `${days} ${days === 1 ? 'dia' : 'dias'}`
  }
  if (minutes >= 60 && minutes % 60 === 0) {
    const hours = minutes / 60
    return `${hours} ${hours === 1 ? 'hora' : 'horas'}`
  }
  return `${minutes} min`
}

export function TestPanel({ agentId, agentName, orgId }: TestPanelProps) {
  const [messages, setMessages] = useState<Msg[]>([])
  const [input, setInput] = useState('')
  const [pendingFile, setPendingFile] = useState<{
    dataUrl: string
    name: string
    type: 'image' | 'audio'
    file: File
  } | null>(null)
  const [isRecording, setIsRecording] = useState(false)
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null)
  const [selectedLead, setSelectedLead] = useState<ContactListItem | null>(null)
  // Campos "salvos" pela IA ao longo da conversa de teste — reenviados a cada
  // turno para simular a qualificação igual à produção (mesmo como anônimo).
  const [simulatedFields, setSimulatedFields] = useState<
    { field: string; value: string }[]
  >([])
  const [pendingBooking, setPendingBooking] =
    useState<PendingBookingConfirmation | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [showLeadList, setShowLeadList] = useState(false)
  const [debouncedSearch, setDebouncedSearch] = useState('')

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const recordingStreamRef = useRef<MediaStream | null>(null)
  const audioChunksRef = useRef<Blob[]>([])
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const testAgent = useTestAgent(orgId, agentId)
  const transcribeAudio = useTranscribeAgentTestAudio(orgId, agentId)
  const { data: followUpConfig, isLoading: followUpLoading } =
    useFollowUpConfig(orgId, agentId)
  const { data: leadsResponse, isLoading: leadsLoading } = useLeads(
    orgId,
    {
      search: debouncedSearch,
      limit: 20,
    },
    showLeadList
  )
  const leads = leadsResponse?.data ?? []

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, testAgent.isPending])

  useEffect(
    () => () => {
      mediaRecorderRef.current?.stop()
      recordingStreamRef.current?.getTracks().forEach((track) => track.stop())
    },
    []
  )

  // Debounce lead search
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
    searchTimerRef.current = setTimeout(
      () => setDebouncedSearch(searchQuery),
      300
    )
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
    }
  }, [searchQuery])

  // Auto-resize textarea
  useEffect(() => {
    const ta = textareaRef.current
    if (!ta) return
    ta.style.height = 'auto'
    ta.style.height = `${Math.min(ta.scrollHeight, 100)}px`
  }, [input])

  function handleReset() {
    setMessages([])
    setPendingFile(null)
    setSimulatedFields([])
    setPendingBooking(null)
    mediaRecorderRef.current?.stop()
    recordingStreamRef.current?.getTracks().forEach((track) => track.stop())
    setIsRecording(false)
  }

  function handleTestFollowUp() {
    if (!followUpConfig?.steps.length) return

    const preview = [...followUpConfig.steps]
      .sort((a, b) => a.position - b.position)
      .map<Msg>((step, index) => ({
        role: 'assistant',
        content:
          step.mode === 'AI'
            ? `Etapa com prompt de IA ainda não suportada pelo executor real:\n${step.content ?? ''}`
            : renderFollowUpContent(step.content ?? '', selectedLead),
        followUp: {
          step: index + 1,
          delayMinutes: step.delay_minutes,
          mode: step.mode,
        },
      }))

    setMessages((current) => [...current, ...preview])
  }

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    const extension = file.name.toLowerCase().split('.').pop()
    const audioExtensions = new Set([
      'aac',
      'flac',
      'm4a',
      'mp3',
      'mp4',
      'oga',
      'ogg',
      'opus',
      'wav',
      'webm',
    ])
    const type =
      file.type.startsWith('audio/') ||
      (extension && audioExtensions.has(extension))
        ? 'audio'
        : 'image'
    if (type === 'audio' && file.size > 20 * 1024 * 1024) return
    const reader = new FileReader()
    reader.onload = () => {
      setPendingFile({
        dataUrl: reader.result as string,
        name: file.name,
        type,
        file,
      })
    }
    reader.readAsDataURL(file)
    e.target.value = ''
  }

  async function handleToggleRecording() {
    if (isRecording) {
      mediaRecorderRef.current?.stop()
      return
    }

    let stream: MediaStream
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch {
      return
    }
    const mimeType =
      ['audio/ogg;codecs=opus', 'audio/mp4', 'audio/webm'].find((type) =>
        MediaRecorder.isTypeSupported(type)
      ) ?? ''
    const recorder = new MediaRecorder(
      stream,
      mimeType ? { mimeType } : undefined
    )
    recordingStreamRef.current = stream
    mediaRecorderRef.current = recorder
    audioChunksRef.current = []
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) audioChunksRef.current.push(event.data)
    }
    recorder.onstop = () => {
      const blob = new Blob(audioChunksRef.current, {
        type: recorder.mimeType || 'audio/webm',
      })
      if (blob.size > 0) {
        const reader = new FileReader()
        reader.onload = () => {
          const extension = recorder.mimeType.includes('mp4')
            ? 'mp4'
            : recorder.mimeType.includes('ogg')
              ? 'ogg'
              : 'webm'
          const file = new File([blob], `gravacao-${Date.now()}.${extension}`, {
            type: blob.type,
          })
          setPendingFile({
            dataUrl: reader.result as string,
            name: 'Áudio gravado',
            type: 'audio',
            file,
          })
        }
        reader.readAsDataURL(blob)
      }
      audioChunksRef.current = []
      recordingStreamRef.current?.getTracks().forEach((track) => track.stop())
      recordingStreamRef.current = null
      mediaRecorderRef.current = null
      setIsRecording(false)
    }
    recorder.start()
    setIsRecording(true)
  }

  async function handleSend() {
    if (
      (!input.trim() && !pendingFile) ||
      testAgent.isPending ||
      transcribeAudio.isPending
    )
      return

    try {
      let transcription: string | undefined
      if (pendingFile?.type === 'audio') {
        const result = await transcribeAudio.mutateAsync(pendingFile.file)
        transcription = result.transcription
      }
      const content =
        transcription || input.trim() || pendingFile?.name || 'Áudio enviado'
      const mediaUrl = pendingFile?.dataUrl
      const mediaType = pendingFile?.type
      const userMsg: AgentTestMessage = {
        role: 'user',
        content,
        mediaUrl: mediaType === 'image' ? mediaUrl : undefined,
      }
      const history: AgentTestMessage[] = [
        ...messages.map((m) => ({
          role: m.role,
          content: m.transcription ?? m.content,
          mediaUrl: m.mediaType === 'image' ? m.mediaUrl : undefined,
          toolMessages: m.toolMessages,
        })),
        userMsg,
      ]
      setMessages((prev) => [
        ...prev,
        {
          role: 'user',
          content:
            mediaType === 'audio' ? pendingFile?.name || 'Áudio' : content,
          mediaUrl,
          mediaType,
          transcription,
        },
      ])
      setInput('')
      setPendingFile(null)

      const res = await testAgent.mutateAsync({
        messages: history,
        contactId: selectedLeadId ?? undefined,
        simulatedFields,
        pendingBooking,
      })
      setPendingBooking(res.pendingBooking ?? null)
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: res.reply,
          actions: res.actions,
          toolMessages: res.toolMessages,
        },
      ])
      // Acumula os SAVE_FIELD da resposta para simular a qualificação nos
      // próximos turnos (último valor por campo prevalece).
      const saved = (res.actions ?? [])
        .filter((action) => action.type === 'SAVE_FIELD')
        .map((action) => ({
          field: String(action.field ?? ''),
          value: String(action.value ?? ''),
        }))
        .filter((entry) => entry.field && entry.value)
      if (saved.length > 0) {
        setSimulatedFields((prev) => {
          const merged = new Map(
            prev.map((entry) => [entry.field, entry.value])
          )
          for (const entry of saved) merged.set(entry.field, entry.value)
          return Array.from(merged, ([field, value]) => ({ field, value }))
        })
      }
    } catch {
      // keep user message, no assistant reply shown
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      void handleSend()
    }
  }

  return (
    <div className="bg-card border-border flex h-full flex-col border-l">
      {/* Header */}
      <div
        className="border-border bg-card flex shrink-0 flex-col gap-2 border-b px-4 py-3"
        onClick={() => setShowLeadList(false)}
      >
        <div className="flex items-center gap-2">
          <Sparkles className="text-primary size-4" strokeWidth={1.5} />
          <span className="text-foreground text-sm font-semibold">
            Testar agente
          </span>
        </div>

        {/* Lead selector */}
        <div className="relative" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            onClick={() => setShowLeadList((p) => !p)}
            className="border-border bg-background hover:border-primary/40 flex w-full items-center gap-2 rounded-md border px-3 py-2 text-left text-xs transition-colors"
          >
            {selectedLead ? (
              <>
                <span className="bg-primary text-primary-foreground flex size-5 shrink-0 items-center justify-center rounded-full text-xs font-bold">
                  {getInitials(selectedLead.name)}
                </span>
                <span className="text-foreground truncate font-medium">
                  {selectedLead.name ?? 'Lead'}
                </span>
                <span className="text-muted-foreground ml-auto shrink-0">
                  {selectedLead.phone ?? ''}
                </span>
              </>
            ) : (
              <>
                <User className="text-muted-foreground size-4 shrink-0" />
                <span className="text-muted-foreground">
                  Nenhum lead (anônimo)
                </span>
                <ChevronDown className="text-muted-foreground ml-auto size-3.5" />
              </>
            )}
          </button>

          {showLeadList && (
            <div className="border-border bg-card absolute top-full right-0 left-0 z-10 mt-1 overflow-hidden rounded-md border shadow-lg">
              <div className="p-2">
                <input
                  type="text"
                  placeholder="Buscar lead..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="border-border bg-background focus:ring-ring w-full rounded-md border px-2.5 py-1.5 text-xs focus:ring-1 focus:outline-none"
                  autoFocus
                />
              </div>
              <div className="max-h-48 overflow-y-auto">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedLeadId(null)
                    setSelectedLead(null)
                    setShowLeadList(false)
                    setMessages([])
                    setSimulatedFields([])
                    setPendingBooking(null)
                  }}
                  className="hover:bg-muted flex w-full items-center gap-2 px-3 py-2 text-xs transition-colors"
                >
                  <User className="text-muted-foreground size-4" />
                  <span className="text-muted-foreground">
                    Nenhum lead (anônimo)
                  </span>
                </button>
                {leadsLoading ? (
                  <p className="text-muted-foreground px-3 py-2 text-xs">
                    Carregando leads...
                  </p>
                ) : null}
                {leads.map((lead) => (
                  <button
                    key={lead.id}
                    type="button"
                    onClick={() => {
                      setSelectedLeadId(lead.id)
                      setSelectedLead(lead)
                      setShowLeadList(false)
                      setSearchQuery('')
                      setMessages([])
                      setSimulatedFields([])
                      setPendingBooking(null)
                    }}
                    className="hover:bg-muted flex w-full items-center gap-2 px-3 py-2 text-xs transition-colors"
                  >
                    <span className="bg-primary text-primary-foreground flex size-5 shrink-0 items-center justify-center rounded-full text-xs font-bold">
                      {getInitials(lead.name)}
                    </span>
                    <span className="text-foreground truncate font-medium">
                      {lead.name ?? 'Lead'}
                    </span>
                    <span className="text-muted-foreground ml-auto shrink-0">
                      {lead.phone ?? ''}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {selectedLead && (
          <p className="text-muted-foreground text-xs">
            A IA já sabe quem é este lead.
          </p>
        )}
      </div>

      {/* Messages */}
      <div
        className="flex flex-1 flex-col gap-3 overflow-y-auto px-4 py-4"
        onClick={() => setShowLeadList(false)}
      >
        {messages.length === 0 && !testAgent.isPending ? (
          <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
            <div className="bg-primary/10 flex size-12 items-center justify-center rounded-full">
              <Sparkles className="text-primary size-6" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-foreground text-sm font-semibold">
                {agentName}
              </p>
              <p className="text-muted-foreground mt-1 text-xs">
                {selectedLead
                  ? `Testando como ${selectedLead.name ?? 'lead'}`
                  : 'Selecione um lead ou escreva como anônimo'}
              </p>
            </div>
          </div>
        ) : (
          <>
            {messages.map((msg, i) => (
              <div
                key={i}
                className={cn(
                  'flex max-w-[90%] gap-2',
                  msg.role === 'user' ? 'ml-auto flex-row-reverse' : ''
                )}
              >
                <div className="mt-0.5 shrink-0">
                  {msg.role === 'user' ? (
                    <div className="bg-muted flex size-7 items-center justify-center rounded-full">
                      {selectedLead ? (
                        <span className="text-foreground text-xs font-bold">
                          {getInitials(selectedLead.name)}
                        </span>
                      ) : (
                        <User className="text-muted-foreground size-4" />
                      )}
                    </div>
                  ) : (
                    <div className="bg-primary flex size-7 items-center justify-center rounded-full">
                      <Sparkles
                        className="text-primary-foreground size-3.5"
                        strokeWidth={2}
                      />
                    </div>
                  )}
                </div>

                <div className="flex flex-col gap-1">
                  {msg.followUp ? (
                    <div className="text-muted-foreground text-2xs flex items-center gap-1 px-1">
                      <Clock3 className="size-3" />
                      Follow-up · etapa {msg.followUp.step} · após{' '}
                      {formatDelay(msg.followUp.delayMinutes)}
                      {msg.followUp.mode === 'AI' ? ' · prompt IA' : ''}
                    </div>
                  ) : null}
                  <div
                    className={cn(
                      'rounded-xl px-3 py-2 text-xs leading-relaxed',
                      msg.role === 'user'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-foreground'
                    )}
                    style={{ whiteSpace: 'pre-wrap' }}
                  >
                    {msg.mediaUrl &&
                      (msg.mediaType === 'audio' ? (
                        <audio
                          src={msg.mediaUrl}
                          controls
                          className="mb-1.5 h-8 max-w-64"
                        />
                      ) : (
                        <img
                          src={msg.mediaUrl}
                          alt="anexo"
                          className="mb-1.5 max-h-40 w-auto rounded-lg object-contain"
                        />
                      ))}
                    {msg.content}
                    {msg.transcription ? (
                      <div className="border-primary-foreground/20 text-2xs mt-2 border-t pt-2">
                        <span className="font-medium">Transcrição:</span>{' '}
                        {msg.transcription}
                      </div>
                    ) : null}
                  </div>
                  {msg.role === 'assistant' &&
                    msg.actions &&
                    msg.actions.length > 0 && (
                      <ActionsDisplay actions={msg.actions} />
                    )}
                </div>
              </div>
            ))}

            {(testAgent.isPending || transcribeAudio.isPending) && (
              <div className="flex gap-2">
                <div className="bg-primary mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full">
                  <Sparkles
                    className="text-primary-foreground size-3.5"
                    strokeWidth={2}
                  />
                </div>
                <div className="bg-muted flex items-center gap-1 rounded-xl px-3 py-2.5">
                  <span
                    className="bg-muted-foreground size-1.5 animate-bounce rounded-full"
                    style={{ animationDelay: '0ms' }}
                  />
                  <span
                    className="bg-muted-foreground size-1.5 animate-bounce rounded-full"
                    style={{ animationDelay: '150ms' }}
                  />
                  <span
                    className="bg-muted-foreground size-1.5 animate-bounce rounded-full"
                    style={{ animationDelay: '300ms' }}
                  />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input */}
      <div
        className="border-border bg-card shrink-0 border-t px-3 py-3"
        onClick={() => setShowLeadList(false)}
      >
        <div className="mb-2 flex items-center justify-between gap-2">
          <p className="text-muted-foreground text-2xs truncate">
            {followUpConfig
              ? `${followUpConfig.name} · ${followUpConfig.steps.length} etapa(s)`
              : 'Nenhum follow-up configurado'}
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 shrink-0 text-xs"
            onClick={handleTestFollowUp}
            disabled={followUpLoading || !followUpConfig?.steps.length}
          >
            <Clock3 className="size-3.5" />
            Simular follow-up
          </Button>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,audio/*"
          className="hidden"
          onChange={handleFileSelect}
        />
        <div className="border-border bg-background focus-within:ring-ring rounded-lg border px-3 py-2 transition-shadow focus-within:ring-1">
          {pendingFile && (
            <div className="relative mb-2 max-w-72">
              {pendingFile.type === 'audio' ? (
                <audio
                  src={pendingFile.dataUrl}
                  controls
                  className="h-9 w-64 max-w-full"
                />
              ) : (
                <img
                  src={pendingFile.dataUrl}
                  alt={pendingFile.name}
                  className="h-16 w-16 rounded-md object-cover"
                />
              )}
              <button
                type="button"
                onClick={() => setPendingFile(null)}
                className="bg-foreground text-background hover:bg-foreground/80 absolute -top-1 -right-1 flex size-4 items-center justify-center rounded-full"
              >
                <X className="size-2.5" />
              </button>
            </div>
          )}
          <div className="flex items-end gap-2">
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Mensagem como o lead enviaria..."
              rows={1}
              maxLength={4000}
              disabled={testAgent.isPending}
              className="text-foreground placeholder:text-muted-foreground min-h-5 flex-1 resize-none bg-transparent text-xs leading-relaxed focus:outline-none"
            />
            <div className="flex shrink-0 items-center gap-1">
              {messages.length > 0 && (
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="size-7"
                  onClick={handleReset}
                  title="Limpar conversa"
                  aria-label="Limpar conversa"
                >
                  <RotateCcw className="size-3.5" />
                </Button>
              )}
              <Button
                type="button"
                size="icon"
                variant={isRecording ? 'destructive' : 'ghost'}
                className="size-7"
                onClick={() => void handleToggleRecording()}
                disabled={testAgent.isPending || transcribeAudio.isPending}
                title={isRecording ? 'Parar gravação' : 'Gravar áudio'}
              >
                {isRecording ? (
                  <Square className="size-3" />
                ) : (
                  <Mic className="size-3.5" />
                )}
              </Button>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="size-7"
                onClick={() => fileInputRef.current?.click()}
                disabled={testAgent.isPending || transcribeAudio.isPending}
                title="Anexar imagem ou áudio"
              >
                <Paperclip className="size-3.5" />
              </Button>
              <Button
                type="button"
                size="icon"
                className="size-7"
                onClick={() => void handleSend()}
                disabled={
                  (!input.trim() && !pendingFile) ||
                  testAgent.isPending ||
                  transcribeAudio.isPending
                }
              >
                {testAgent.isPending || transcribeAudio.isPending ? (
                  <span className="border-primary-foreground/30 border-t-primary-foreground size-3.5 animate-spin rounded-full border-2" />
                ) : (
                  <Send className="size-3.5" />
                )}
              </Button>
            </div>
          </div>
        </div>
        <p className="text-muted-foreground mt-1.5 px-1 text-xs">
          {transcribeAudio.isPending
            ? 'Transcrevendo áudio...'
            : 'Enter para enviar · Shift+Enter para nova linha'}
        </p>
      </div>
    </div>
  )
}
